import { FormSectionHandler } from '../entity.handler';
import { AlertResponse, AlertUpdateRequest, IdName, EventFilter, IoTypeListItem, AlertAction } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, EventFilterField, ComboField, CostCentreField } from '../utils';
import { ICONS } from '../icons';
import { nullAccessDenied } from 'app/services';
import { EventFilterService } from 'app/services/events/event-filter.service';
import { AlertNotify } from '@key-telematics/fleet-api-client';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import objectHash from 'object-hash';
import { KuiTreeSelectNode } from 'app/key-ui/tree-select/tree-select.component';

interface EditorValues {
    name?: string;
    costCentre?: IdName;
    filter?: EventFilter;
    priority?: string;
    notify?: ListFieldValue<AlertNotify>[];
    actions?: ListFieldValue<AlertAction>[];

}

interface AlertActionItem {
    data: AlertAction;
    description: string;
    icon: string;
    id: string;
    name: string;
}

export class AlertDetailsSectionHandler implements FormSectionHandler<AlertResponse, EditorValues> {

    type = 'form';
    icon = ICONS.alert;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');
    warning = null;

    notificationTargets: { id: string, name: string }[]; // needed for lookups
    ioTypes: IoTypeListItem[] = [];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private creating: boolean,
        private filters: EventFilterService
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ALERT.DETAILS.${key}`, values);
    }

    applyUpdate(entity: AlertResponse, payload: AlertUpdateRequest): Promise<AlertResponse> {
        return this.admin.applyUpdate(api => api.entities.updateAlert(entity.id, payload));
    }

    async mapEntityToEditor(entity: AlertResponse): Promise<EditorValues> {
        this.ioTypes = (await this.admin.getCached(`listIoTypes/${this.ownerId}/true/0/300`, api => api.entities.listIoTypes(this.ownerId, true, 0, 300), 60)).items;
        const result = {
            name: entity.name,
            costCentre: entity.costCentre,
            filter: entity.filter,
            priority: entity.priority,
            notify: (entity.notify || []).map(notify => this.notifyToListItem(notify)),
            actions: (entity.actions || []).map(action => this.actionToListItem(action)),
        };
        Object.keys(result).forEach(key => result[key] === undefined && delete result[key]); // strip undefined properties from the object
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: AlertUpdateRequest, values: EditorValues): Promise<AlertUpdateRequest> {
        let filter;
        if (values?.filter?.action?.eventClass === 'ai') {
            filter = {...values.filter};
            values.filter.action.eventClass = 'triggeredevent';
        } else {
            filter = values.filter;
        }

        const patch: AlertUpdateRequest = {
            name: values.name,
            costCentre: values.costCentre,
            filter: filter,
            priority: values.priority as any,
            notify: values.notify && values.notify.map(x => x.data),
            actions: values.actions && values.actions.map(x => x.data),
        };
        Object.keys(patch).forEach(key => patch[key] === undefined && delete patch[key]); // strip undefined properties from the object
        return Promise.resolve(EntityUtils.mergeFields(['name', 'costCentre', 'filter', 'priority', 'notify', 'actions'], patch, entity));
    }

    getFields(entity: AlertResponse): Promise<FormBuilderField[]> {
        return Promise.all([
            this.admin.getCached(`listUserRoles/${this.ownerId}/0/100`, api => api.accounts.listUserRoles(this.ownerId, 0, 100)).catch(nullAccessDenied),
            this.admin.getCached(`listUsers/${this.ownerId}/0/100/null/state=active`, api => api.accounts.listUsers(this.ownerId, 0, 100, null, 'state=active')).catch(nullAccessDenied),
        ]).then(([roles, users]) => {

            if (roles) {
                roles.items.sort((a, b) => a.name.localeCompare(b.name));
            }
            if (users) {
                users.items.sort((a, b) => a.name.localeCompare(b.name));
            }

            const notifyTree = [
                { id: 'user.' + this.admin.user.id, name: this.admin.translate(`EDITORS.ALERT.NOTIFY.ME`) },
                roles && { id: 'roles', name: this.admin.translate(`EDITORS.ALERT.NOTIFY.ROLES`), children: roles.items.map(role => ({ id: 'role.' + role.id, name: role.name })) },
                users && { id: 'specific', name: this.admin.translate(`EDITORS.ALERT.NOTIFY.USERS`), children: users.items.map(user => ({ id: 'user.' + user.id, name: user.name })) },
            ].filter(x => x);


            const actionTree = [
                {
                    id: 'camera', name: this.admin.translate(`EDITORS.ALERT.ACTIONS.CAMERA`),
                    children: [
                        { id: 'request_video', name: this.admin.translate(`EDITORS.ALERT.ACTIONS.REQUEST_VIDEO`) },
                    ].filter(x => x && this.admin.features.page.alerts.actions.includes(x.id)),
                },
                {
                    id: 'labels', name: this.admin.translate(`EDITORS.ALERT.ACTIONS.LABELS`),
                    children: [
                        { id: 'label_current_trip', name: this.admin.translate(`EDITORS.ALERT.ACTIONS.LABEL_CURRENT_TRIP`) },
                    ].filter(x => x && this.admin.features.page.alerts.actions.includes(x.id)),
                },
            ].filter(x => x && x.children.length > 0);

            this.notificationTargets = [
                { id: 'user.' + this.admin.user.id, name: this.admin.user.name },
                ...(roles ? roles.items : []).map(role => ({ id: 'role.' + role.id, name: role.name })),
                ...(users ? users.items : []).map(user => ({ id: 'user.' + user.id, name: user.name })),
            ];

            const priorities = ['low', 'medium', 'high'].map(key => ({ key: key, value: this.admin.translate(`EDITORS.ALERT.PRIORITIES.${key.toUpperCase()}`) }));
            return [
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                CostCentreField(this.ownerId, 'costCentre', this.translate('FIELDS.COST_CENTRE'), true),
                ComboField('priority', this.translate('FIELDS.PRIORITY'), true, priorities),
                EventFilterField('filter', this.translate('FIELDS.FILTER'), true, this.ownerId, true, this.filters, ['costcentres', 'groups', 'types']),
                {
                    id: 'notify', title: this.translate('FIELDS.NOTIFY'), type: 'list', required: false,
                    actions: [
                        { name: this.admin.i18n.instant('SHARED.ADD'), dropdownValues: notifyTree, click: this.addNotification.bind(this) },
                    ],
                    getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
                },
                !this.creating && this.admin.features.page.alerts.actions && this.admin.features.page.alerts.actions.length > 0 && {
                    id: 'actions', title: this.translate('FIELDS.ACTIONS'), type: 'list', required: false,
                    actions: [
                        { name: this.admin.i18n.instant('SHARED.ADD'), dropdownValues: actionTree, click: this.addAction.bind(this) },
                        { id: 'edit', click: this.addAction.bind(this), },
                    ],
                    getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
                },
            ].filter(x => x);
        }).then(fields => EntityUtils.prepareBulk(!entity, [], fields));

    }

    notifyToListItem(notify: AlertNotify): ListFieldValue<AlertNotify> {
        return {
            id: notify.id,
            name: notify.name,
            icon: notify.type === 'user' ? ICONS.user : ICONS.userrole,
            description: notify.type === 'user' ? this.admin.translate(`EDITORS.ALERT.NOTIFY.USER`) : this.admin.translate(`EDITORS.ALERT.NOTIFY.ROLE`),
            data: notify,
        };
    }

    actionToListItem(item: AlertAction): ListFieldValue<AlertAction> {
        const icons = {
            'request_video': 'video',
            'label_current_trip': ICONS.label,
        };
        return {
            id: item.id,
            name: this.admin.translate(`EDITORS.ALERT.ACTIONS.${item.type.toUpperCase()}`),
            icon: icons[item.type],
            description: this.admin.translate(`EDITORS.ALERT.ACTIONS.${item.type.toUpperCase()}_DESC`, {
                ...item.options,
                cameras: (item.options.cameras || []).map(id => {
                    const iot = this.ioTypes.find(x => x.id === id);
                    return (iot && iot.name) || id;
                }).join(','),
                labels: '\n' + (item.options.labels || []).map(x => `* ${x.name}`).join('\n'),
            }),
            data: item,
        };
    }

    addNotification(itemId: string, field: FormBuilderField, values: EditorValues): Promise<void> {
        const items = values[field.id];
        const [type, id] = itemId.split('.');
        if (type === 'user' || type === 'role') {
            const item = this.notificationTargets.find(x => x.id === itemId);
            const existing = items.find(x => x.data.id === id && x.data.type === type);
            if (!existing) {
                items.push(this.notifyToListItem({ id: id, type: type as any, name: item.name }));
            }
        }
        return Promise.resolve();

    }

    async addAction(itemId: string, field: FormBuilderField, values: EditorValues, item: AlertActionItem): Promise<void> {
        let options: any;

        if (itemId === 'edit') {
            itemId = item.data?.type;
            options = item.data.options;
        }

        switch (itemId) {
            case 'request_video':
                options = await this.actionVideoRequestModal(options);
                break;
            case 'label_current_trip':
                options = await this.actionLabelCurrentTripModal(options);
                break;
            default:
                return;
        }
        if (options) {
            const id = objectHash(options).substr(0, 16);
            const items = values[field.id];
            const existing = items.find(x => x && item && x.id === item.id);
            const idx = items.findIndex(x => x.id === existing?.id);
            const type: any = item ? item.data.type : itemId;
            
            if (existing) {
                items[idx] = this.actionToListItem({ id: item.id, type: type, options: options });
            } else {
                items.push(this.actionToListItem({ id: id, type: type, options: options }));
            }
        }
    }

    async actionVideoRequestModal(options: { cameras: string[], duration: number, offset: number }): Promise<{ cameras: string[], duration: number, offset: number }> {

        const cameras = this.ioTypes.filter(x => x.type === 'camera_input' && x.state === 'active').map(item => ({ key: item.id, value: item.name }));

        // This method doesn't respect the various cameras supported max durations...
        // Default the max duration allowed to either the value set by the client or 60 seconds, whichever is smaller.
        const clientDuration = this.admin.features.page.videos.maxDuration;
        let newMax = Math.min(clientDuration, 60);
        newMax = newMax > 0 ? newMax : 1;

        const result = await this.admin.modal.form({
            groups: [{
                name: this.admin.i18n.instant('MEDIA.VIDEO_REQUEST.MODAL.NAME'),
                description: this.admin.i18n.instant('MEDIA.VIDEO_REQUEST.MODAL.DESCRIPTION'),
                fields: [
                    { id: 'cameras', type: 'checklist', title: this.admin.i18n.instant('MEDIA.VIDEO_REQUEST.MODAL.FIELDS.CAMERAS'), required: true, values: cameras },
                    { id: 'duration', type: 'number', title: this.admin.i18n.instant('MEDIA.VIDEO_REQUEST.MODAL.FIELDS.DURATION'), required: true, min: 5, max: newMax, unit: this.admin.i18n.instant('MEDIA.VIDEO_REQUEST.MODAL.FIELDS.SECONDS') },
                    { id: 'offset', type: 'number', title: this.admin.i18n.instant('MEDIA.VIDEO_REQUEST.MODAL.FIELDS.OFFSET'), required: true, min: -600, max: 600, unit: this.admin.i18n.instant('MEDIA.VIDEO_REQUEST.MODAL.FIELDS.SECONDS'), description: this.admin.i18n.instant('MEDIA.VIDEO_REQUEST.MODAL.OFFSET_DESCRIPTION')},
                ],
            }],
        }, options || {
            duration: Math.min(newMax, 30),
            offset: -10,
            cameras: cameras.map(x => x.key), // default to all
        });

        return result;
    }

    async actionLabelCurrentTripModal(options: { labels: IdName[] }): Promise<{ labels: IdName[] }> {
        const ttlSeconds = 0; // in case a label was deleted in last 60 seconds (the default cache ttl)
        const labels = await this.admin.getCached(`listLabels/${this.ownerId}/true/0/100/name/state=active`, api => api.entities.listLabels(this.ownerId, true, 0, 100, 'name', 'state=active').then(x => x.items), ttlSeconds);

        const labelTree: KuiTreeSelectNode[] = [];
        for (const item of labels) {
            if (item.values && item.values.length) {
                for (const sub of item.values) {
                    labelTree.push({
                        id: item.label + '/' + sub.label,
                        name: [item.name, sub.name].filter(x => x).join(': '),
                        icon: ICONS.label,
                    });
                }
            } else {
                labelTree.push({
                    id: item.label,
                    name: item.name,
                    icon: ICONS.label,
                });
            }
        }

        const valueToListItem = (value: string): ListFieldValue<string> => {
            const [rootLabel, subLabel] = value.split('/');
            const label1 = labels.find(x => x.label === rootLabel);
            if (label1) {
                const label2 = label1.values.find(x => x.label === subLabel);
                return {
                    id: value,
                    name: [label1.name, label2?.name].filter(x => x).join(': '),
                    icon: ICONS.label,
                    color: label2?.color || label1.color,
                    data: value,
                };
            } else {
                return {
                    id: value,
                    name: value,
                    icon: ICONS.label,
                    data: value,
                };
            }
        }        

        const result = await this.admin.modal.form({
            groups: [{
                name: this.admin.translate('EDITORS.ALERT.ACTIONS.LABEL_MODAL.TITLE'),
                description: this.admin.translate('EDITORS.ALERT.ACTIONS.LABEL_MODAL.DESC'),
                fields: [
                    {
                        id: 'labels', title: this.admin.translate('EDITORS.ALERT.ACTIONS.LABEL_MODAL.FIELDS.LABELS'), type: 'list', required: false,
                        actions: [
                            {
                                name: this.admin.i18n.instant('SHARED.ADD'), dropdownValues: labelTree, click: async (id, _field, values) => {
                                    if (!values.labels.find(x => x.id === id)) {
                                        // remove any existing labels that are sub labels of this label
                                        const [root] = id.split('/');
                                        values.labels = values.labels.filter(x => !x.id.startsWith(root + '/'));
                                        values.labels.push(valueToListItem(id));
                                    }
                                }
                            }
                        ],

                    },
                ],
            }],
        }, {
            labels: (options?.labels || []).map(x => valueToListItem(x.id)),
        });

        if (result) {
            return {
                labels: result.labels.map(x => ({ id: x.id, name: x.name })),
            };
        } else {
            return options;
        }
    }

}
